import { Certificate, CertificateFont } from './types';

export class PositionedText {
  constructor(
    public text: string,
    public x: number,
    public y: number,
    public font: string,
    public color: string,
    public align: 'left' | 'center' | 'right' = 'right',
    public letterSpacing?: string,
    public caps?: boolean
  ) {
    if (caps) {
      this.text = text.toUpperCase();
    }
  }

  static usernameFromCerficicateDesign(certificateDesign: Certificate) {
    const usernameFont = certificateDesign.settings.fonts.userName;
    const usernameColor = certificateDesign.settings.colors[certificateDesign.settings.colors.userNameColor ?? 'text'];
    const position = certificateDesign.settings.positions.userName;
    const font = [usernameFont.size, usernameFont.font];

    if (usernameFont.weight) {
      font.unshift(usernameFont.weight);
    }

    if (usernameFont.style) {
      font.unshift(usernameFont.style);
    }

    return new PositionedText(
      certificateDesign.userName,
      position.x,
      position.y,
      font.join(' '),
      usernameColor,
      position.align,
      usernameFont.letterSpacing,
      usernameFont.caps
    );
  }

  static certificateNameFromCerficicateDesign(certificateDesign: Certificate) {
    const certificateNameFont = certificateDesign.settings.fonts.certificateName;
    const certificateNameColor =
      certificateDesign.settings.colors[certificateDesign.settings.colors.certificateNameColor ?? 'text'];
    const position = certificateDesign.settings.positions.certificateName;

    const font = [certificateNameFont.size, certificateNameFont.font];

    if (certificateNameFont.weight) {
      font.unshift(certificateNameFont.weight);
    }

    if (certificateNameFont.style) {
      font.unshift(certificateNameFont.style);
    }

    return new PositionedText(
      certificateDesign.certificateName,
      position.x,
      position.y,
      font.join(' '),
      certificateNameColor,
      position.align,
      certificateNameFont.letterSpacing,
      certificateNameFont.caps
    );
  }

  static dateStringFromCerficicateDesign(certificateDesign: Certificate) {
    const dateStringFont = certificateDesign.settings.fonts.dateString;
    const dateStringColor = certificateDesign.settings.colors[certificateDesign.settings.colors.dateColor ?? 'text'];
    const position = certificateDesign.settings.positions.dateString;

    const font = [dateStringFont.size, dateStringFont.font];

    if (dateStringFont.weight) {
      font.unshift(dateStringFont.weight);
    }

    if (dateStringFont.style) {
      font.unshift(dateStringFont.style);
    }

    return new PositionedText(
      certificateDesign.dateString,
      position.x,
      position.y,
      font.join(' '),
      dateStringColor,
      position.align,
      dateStringFont.letterSpacing,
      dateStringFont.caps
    );
  }

  static bylineFromCerficicateDesign(certificateDesign: Certificate) {
    const bylineText = certificateDesign.byline ?? 'has been awarded a certificate of completion for';
    const bylineFont =
      certificateDesign.settings.fonts.byline ??
      ({
        font: certificateDesign.settings.fonts.default,
        size: '21px',
      } as CertificateFont);
    const bylinePosition = certificateDesign.settings.positions.byline ?? {
      x: certificateDesign.settings.positions.userName.x,
      y:
        certificateDesign.settings.positions.userName.y +
        Number(certificateDesign.settings.fonts.userName.size.replace(/\D/g, '')) / 2,
    };

    const font = [bylineFont.size, bylineFont.font];

    if (bylineFont.weight) {
      font.unshift(bylineFont.weight);
    }

    if (bylineFont.style) {
      font.unshift(bylineFont.style);
    }

    return new PositionedText(
      bylineText,
      bylinePosition.x,
      bylinePosition.y,
      font.join(' '),
      certificateDesign.settings.colors.text,
      bylinePosition.align,
      bylineFont.letterSpacing,
      bylineFont.caps
    );
  }

  static creditsFromCerficicateDesign(certificateDesign: Certificate) {
    if (!certificateDesign.credits) {
      return undefined;
    }

    const courseText = `${certificateDesign.credits} credits`;
    const courseTextFont =
      certificateDesign.settings.fonts.credits ??
      ({
        font: certificateDesign.settings.fonts.default,
        size: '21px',
      } as CertificateFont);
    const courseDataPosition = certificateDesign.settings.positions.credits ?? {
      x: certificateDesign.settings.positions.userName.x,
      y:
        certificateDesign.settings.positions.userName.y +
        Number(certificateDesign.settings.fonts.userName.size.replace(/\D/g, '')) / 2,
    };

    const font = [courseTextFont.size, courseTextFont.font];

    if (courseTextFont.weight) {
      font.unshift(courseTextFont.weight);
    }

    if (courseTextFont.style) {
      font.unshift(courseTextFont.style);
    }

    return new PositionedText(
      courseText,
      courseDataPosition.x,
      courseDataPosition.y,
      font.join(' '),
      certificateDesign.settings.colors[certificateDesign.settings.colors.creditColor ?? 'text'],
      courseDataPosition.align,
      courseTextFont.letterSpacing,
      courseTextFont.caps
    );
  }

  static categoriesFromCerficicateDesign(certificateDesign: Certificate) {
    if (!certificateDesign.categories) {
      return undefined;
    }

    const courseText = `${certificateDesign.categories?.join(', ')}`;
    const courseTextFont =
      certificateDesign.settings.fonts.categories ??
      ({
        font: certificateDesign.settings.fonts.default,
        size: '21px',
      } as CertificateFont);
    const courseDataPosition = certificateDesign.settings.positions.categories ?? {
      x: certificateDesign.settings.positions.userName.x,
      y:
        certificateDesign.settings.positions.userName.y +
        Number(certificateDesign.settings.fonts.userName.size.replace(/\D/g, '')) / 2,
    };

    const font = [courseTextFont.size, courseTextFont.font];

    if (courseTextFont.weight) {
      font.unshift(courseTextFont.weight);
    }

    if (courseTextFont.style) {
      font.unshift(courseTextFont.style);
    }

    return new PositionedText(
      courseText,
      courseDataPosition.x,
      courseDataPosition.y,
      font.join(' '),
      certificateDesign.settings.colors[certificateDesign.settings.colors.categoriesColor ?? 'text'],
      courseDataPosition.align,
      courseTextFont.letterSpacing,
      courseTextFont.caps
    );
  }

  public getFontSize() {
    const match = this.font.match(/\d+/);
    if (match) {
      return parseInt(match[0]);
    }
    return 20;
  }
}
